/**
 * Calendar Service
 * Unified calendar data service for paid campaigns and organic posts
 */

import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Get combined calendar data for a date range
 * @param {string} brandId - Brand ID
 * @param {Date} startDate - Start of date range
 * @param {Date} endDate - End of date range
 * @returns {Promise<Object>} Combined calendar data
 */
export const getCombinedCalendarData = async (brandId, startDate, endDate) => {
    try {
        const [campaigns, posts] = await Promise.all([
            getPaidCampaigns(brandId, startDate, endDate),
            getOrganicPosts(brandId, startDate, endDate)
        ]);

        return {
            campaigns,
            posts,
            totalItems: campaigns.length + posts.length
        };
    } catch (error) {
        console.error('Error fetching combined calendar data:', error);
        throw error;
    }
};

/**
 * Get paid campaigns for a date range
 * @param {string} brandId - Brand ID
 * @param {Date} startDate - Start of date range
 * @param {Date} endDate - End of date range
 * @returns {Promise<Array>} Array of campaigns
 */
export const getPaidCampaigns = async (brandId, startDate, endDate) => {
    try {
        // Get from funnels collection
        const funnelsRef = collection(db, 'funnels');
        const q = query(
            funnelsRef,
            where('brandId', '==', brandId),
            orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const campaigns = [];

        snapshot.forEach(doc => {
            const funnel = { id: doc.id, ...doc.data() };

            // Extract campaign nodes from funnel
            if (funnel.nodes) {
                funnel.nodes
                    .filter(node => node.type === 'campaign')
                    .forEach(node => {
                        const campaign = {
                            id: node.id,
                            funnelId: funnel.id,
                            name: node.data.name,
                            objective: node.data.objective,
                            platform: node.data.platform || funnel.platform,
                            budget: node.data.budget,
                            schedule: node.data.schedule,
                            funnelStage: node.data.funnelStage,
                            status: node.data.status || 'draft'
                        };

                        // Check if campaign falls within date range
                        if (campaign.schedule?.start) {
                            const campaignStart = new Date(campaign.schedule.start);
                            const campaignEnd = campaign.schedule.end
                                ? new Date(campaign.schedule.end)
                                : null;

                            if (
                                (campaignStart <= endDate) &&
                                (!campaignEnd || campaignEnd >= startDate)
                            ) {
                                campaigns.push(campaign);
                            }
                        } else {
                            // Include campaigns without schedules
                            campaigns.push(campaign);
                        }
                    });
            }
        });

        return campaigns;
    } catch (error) {
        console.error('Error fetching paid campaigns:', error);
        throw error;
    }
};

/**
 * Get organic posts for a date range
 * @param {string} brandId - Brand ID
 * @param {Date} startDate - Start of date range
 * @param {Date} endDate - End of date range
 * @returns {Promise<Array>} Array of posts
 */
export const getOrganicPosts = async (brandId, startDate, endDate) => {
    try {
        const postsRef = collection(db, 'posts');
        const q = query(
            postsRef,
            where('brandId', '==', brandId),
            where('scheduledDate', '>=', startDate.toISOString()),
            where('scheduledDate', '<=', endDate.toISOString()),
            orderBy('scheduledDate', 'asc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching organic posts:', error);
        throw error;
    }
};

/**
 * Get calendar items for a specific date
 * @param {string} brandId - Brand ID
 * @param {Date} date - The specific date
 * @returns {Promise<Object>} Items for that date
 */
export const getItemsForDate = async (brandId, date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return getCombinedCalendarData(brandId, startOfDay, endOfDay);
};

/**
 * Get calendar summary for a month
 * @param {string} brandId - Brand ID
 * @param {number} year - Year
 * @param {number} month - Month (0-11)
 * @returns {Promise<Object>} Monthly summary
 */
export const getMonthSummary = async (brandId, year, month) => {
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    const data = await getCombinedCalendarData(brandId, startDate, endDate);

    // Calculate summary stats
    let totalBudget = 0;
    const budgetByPlatform = {};

    data.campaigns.forEach(campaign => {
        const amount = campaign.budget?.amount || 0;
        totalBudget += amount;

        const platform = campaign.platform || 'other';
        budgetByPlatform[platform] = (budgetByPlatform[platform] || 0) + amount;
    });

    return {
        ...data,
        summary: {
            campaignCount: data.campaigns.length,
            postCount: data.posts.length,
            totalBudget,
            budgetByPlatform,
            activeDays: getActiveDays(data)
        }
    };
};

/**
 * Calculate which days have content
 */
const getActiveDays = (data) => {
    const days = new Set();

    // Add days from campaign schedules
    data.campaigns.forEach(campaign => {
        if (campaign.schedule?.start) {
            const start = new Date(campaign.schedule.start);
            const end = campaign.schedule.end
                ? new Date(campaign.schedule.end)
                : start;

            let current = new Date(start);
            while (current <= end) {
                days.add(current.toISOString().split('T')[0]);
                current.setDate(current.getDate() + 1);
            }
        }
    });

    // Add days from posts
    data.posts.forEach(post => {
        if (post.scheduledDate) {
            const date = new Date(post.scheduledDate);
            days.add(date.toISOString().split('T')[0]);
        }
    });

    return Array.from(days).sort();
};

export default {
    getCombinedCalendarData,
    getPaidCampaigns,
    getOrganicPosts,
    getItemsForDate,
    getMonthSummary
};
